import React from "react";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { QueryClient, QueryClientProvider } from "react-query";
// import { UserbackProvider } from "@userback/react";
import { HelmetProvider } from "react-helmet-async";
import Modal from "react-modal";
import axios from "axios";
import App from "./App";
import { store } from "./app/store";
import reportWebVitals from "./reportWebVitals";

import "./config";
import "./index.css";
import "./assets/custom.css";

// Dozens of components across the app do `JSON.parse(localStorage.getItem("settings"))`
// with no fallback, then read a field straight off the result (e.g. `.currency`).
// Whenever that key is missing — right after a logout/localStorage.clear(), or a
// fresh session before the settings API call has written it — JSON.parse(null)
// returns null and the very next property read throws, crashing the whole React
// tree (looks exactly like an unexplained logout/blank page). Guaranteeing the
// key always holds valid JSON before anything renders closes that off everywhere
// at once, without touching every one of those call sites individually.
try {
  const rawSettings = localStorage.getItem("settings");
  if (rawSettings) {
    JSON.parse(rawSettings);
  } else {
    localStorage.setItem("settings", "{}");
  }
} catch (e) {
  localStorage.setItem("settings", "{}");
}

// Global auto-logout when the session token is no longer valid.
// A bare "any 401 logs you out" interceptor caused random logouts before:
// some controllers use 401 for things like a wrong password, and requests
// fired before the token is attached 401 too. So we only log out when the
// token that was sent is itself past its `exp` (or unreadable), or the auth
// middleware explicitly rejected it ("Invalid or expired token" is only sent
// when jwt.verify fails, e.g. a bad signature) — the cases where retrying
// can never succeed and the user must sign in again.
const isTokenExpired = (token) => {
  try {
    const payload = JSON.parse(
      atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))
    );
    return !payload.exp || payload.exp * 1000 <= Date.now();
  } catch (e) {
    return true;
  }
};

let sessionExpiredRedirecting = false;
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // Fast page-to-page navigation aborts in-flight requests from the page
    // just left; a canceled request must never be treated as a real 401.
    if (axios.isCancel(error) || error.code === "ERR_CANCELED") {
      return Promise.reject(error);
    }
    const authHeader = error.config?.headers?.Authorization || "";
    const token = authHeader.startsWith("Bearer ") ? authHeader.slice(7) : "";
    const tokenRejected =
      error.response?.data?.message === "Invalid or expired token";
    if (
      error.response?.status === 401 &&
      token &&
      (isTokenExpired(token) || tokenRejected) &&
      !sessionExpiredRedirecting
    ) {
      const isMaster = window.location.pathname.startsWith("/master");
      const loginPath = isMaster ? "/master-user/login" : "/login";
      if (window.location.pathname !== loginPath) {
        // Several requests usually 401 at once; only the first one redirects.
        sessionExpiredRedirecting = true;
        localStorage.removeItem(isMaster ? "masterUser" : "user");
        window.location.href = loginPath;
      }
    }
    return Promise.reject(error);
  }
);

// After a frontend redeploy, a tab that's been open since before the deploy
// still holds JS chunk hashes that no longer exist on the server. The next
// route-based lazy import for that tab 404s, and the app is stuck broken
// until the user manually refreshes. Auto-reload once when that happens so
// the tab picks up the new build itself. The sessionStorage guard stops a
// reload loop if the chunk is missing for some other persistent reason.
window.addEventListener("error", (event) => {
  const message = event?.message || event?.error?.message || "";
  if (/loading chunk .* failed/i.test(message) || /failed to fetch dynamically imported module/i.test(message)) {
    if (!sessionStorage.getItem("chunkReloadAttempted")) {
      sessionStorage.setItem("chunkReloadAttempted", "1");
      window.location.reload();
    }
  }
});
window.addEventListener("unhandledrejection", (event) => {
  const message = event?.reason?.message || "";
  if (/loading chunk .* failed/i.test(message) || /failed to fetch dynamically imported module/i.test(message)) {
    if (!sessionStorage.getItem("chunkReloadAttempted")) {
      sessionStorage.setItem("chunkReloadAttempted", "1");
      window.location.reload();
    }
  }
});

// "ResizeObserver loop completed with undelivered notifications" is a spec-defined
// browser behavior: if a ResizeObserver callback itself causes another resize
// within the same observation cycle (e.g. rsuite's date-range picker positioning
// its popup on open), the browser reports a loop error once the iteration limit
// is hit. Trying to catch/suppress that report after the fact is a losing race
// against CRA's dev-mode error overlay, which registers its own handler before
// this app's code runs and can intercept it through more than one path.
// The real, standard fix: stop the condition from ever occurring by deferring
// every ResizeObserver callback to the next animation frame, so it never runs
// synchronously inside the resize-observation cycle that triggers the loop
// check in the first place. This patches the native constructor globally, so
// every ResizeObserver used anywhere in the app (rsuite, the `resize-observer`
// polyfill fallback, etc.) is covered without touching each call site.
(function patchResizeObserverLoop() {
  if (typeof window === "undefined" || !window.ResizeObserver) return;
  const OriginalResizeObserver = window.ResizeObserver;
  window.ResizeObserver = class PatchedResizeObserver extends OriginalResizeObserver {
    constructor(callback) {
      super((entries, observer) => {
        window.requestAnimationFrame(() => {
          callback(entries, observer);
        });
      });
    }
  };
})();

Modal.setAppElement("#root");
Modal.defaultProps = {
  ...Modal.defaultProps,
  closeTimeoutMS: 420,
  bodyOpenClassName: "",
  htmlOpenClassName: "",
  parentSelector: () => document.getElementById("content-modal-root") || document.body,
};

// Create a new QueryClient instance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Get the root container from the DOM
const container = document.getElementById("root");

// Ensure the container exists before creating the root
if (container) {
  const root = createRoot(container);
  console.warn = () => { };

  // Render the application
  root.render(
    <React.StrictMode>
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <Provider store={store}>
            <App />
          </Provider>
        </QueryClientProvider>
      </HelmetProvider>
    </React.StrictMode>
  );

  // Measure performance of the app
  reportWebVitals();
} else {
  console.error("Root container not found. Failed to render the application.");
}